import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { CreditCardFormAPI } from '@wix/payments-cc-form/dist/lib/CreditCardForm';
import { ValidateResult } from '@wix/payments-cc-form/dist/src/types/ValidateResult';
import cn from 'classnames';
import React from 'react';
import {
  AppInstance,
  RevalidateAppInstance,
} from '@wix/cashier-common/dist/src/types/AppInstance';
import { Theme } from '../../types/Theme';
import { AsyncJobManager } from '../../utils/AsyncJobManager';
import { DH } from '../../utils/DataHook';
import { ActivePaymentMethod } from '../CashierPaymentsWidget/types';
import { CreditCard } from './PaymentMethods/CreditCard';
import { InPerson } from './PaymentMethods/InPerson';
import { Offline } from './PaymentMethods/Offline';
import { CommonMethodProps, SavedCardProp } from './PaymentMethods/types';
import { Wallet } from './PaymentMethods/Wallet';
import { isAdaptedComponentsEnabled } from '../../hooks/useAdaptedComponents';
import * as s from './styles.scss';
import { AppId } from '@wix/cashier-common/dist/src/enums/AppId';

const enum Job {
  CardMethodLoaded,
}

export interface PaymentMethodScreenControllerProps {
  amount?: string;
  currency?: string;
  appId?: AppId;
  appInstanceId?: string;
  msid?: string;
  countryCode?: string;
  locale?: string;
  paymentMethod: ActivePaymentMethod;
  paymentsSDK: any;
  orderId: string;
  visitorId: string;
  siteOwnerId: string;
  ccFormUrlOverride?: string;
  isSaveCCEnabled: boolean;
  savedCard?: SavedCardProp;
  theme?: Theme;
  deviceType?: any;
  shouldApplySiteStyles?: boolean;
  isWalletWithTitle?: boolean;
  isSinglePaymentOption?: boolean;
  tokenizeMode?: boolean;
  appInstance?: AppInstance;
  isMerchantContext?: boolean;

  onLoadingChanged?(key: string, status: boolean): void;
  onFullLoad?(): void;
  onCardFormApiInit(api: CreditCardFormAPI): void;
  onCardFieldChange?(validityState: ValidateResult): void;
  revalidateAppInstance?: RevalidateAppInstance;
}

export class PaymentMethodScreenController extends React.Component<PaymentMethodScreenControllerProps> {
  static defaultProps: Partial<PaymentMethodScreenControllerProps> = {
    deviceType: 'desktop',
  };

  private jobManager = new AsyncJobManager<Job>();

  constructor(props: PaymentMethodScreenControllerProps) {
    super(props);

    if (props.paymentMethod.paymentMethod === PaymentMethod.CreditCard) {
      this.jobManager.add(Job.CardMethodLoaded);
    }
  }

  componentDidUpdate(prevProps: PaymentMethodScreenControllerProps) {
    if (
      prevProps.paymentMethod.paymentMethod !==
        this.props.paymentMethod.paymentMethod &&
      prevProps.paymentMethod.paymentMethod === PaymentMethod.CreditCard
    ) {
      // to release "onFullLoad" (that would otherwise be hanging)
      this.jobManager.remove(Job.CardMethodLoaded);
    }
  }

  componentDidMount() {
    this.jobManager.doneAll().then(() => {
      this.props.onFullLoad?.();
    });
  }

  renderCreditCard(commonProps: CommonMethodProps) {
    const { connectedProviderId, additionalInfo } = this.props.paymentMethod;

    const props: React.ComponentProps<typeof CreditCard> = {
      ...commonProps,
      ccFormUrlOverride: this.props.ccFormUrlOverride,
      appId: this.props.appId,
      appInstanceId: this.props.appInstanceId,
      appInstance: this.props.appInstance,
      locale: this.props.locale,
      msid: this.props.msid,
      visitorId: this.props.visitorId,
      siteOwnerId: this.props.siteOwnerId,
      installments: additionalInfo?.installments,
      paymentsSDK: this.props.paymentsSDK,
      tokenizeMode: this.props.tokenizeMode,
      isSaveCCEnabled: this.props.isSaveCCEnabled,
      savedCard: this.props.savedCard,
      providerId: connectedProviderId as PaymentProvider,
      deviceType: this.props.deviceType,
      countryCode: this.props.countryCode,
      orderId: this.props.orderId,
      onLoad: () => {
        this.props.onLoadingChanged?.('CreditCard', false);
        this.jobManager.finish(Job.CardMethodLoaded);
      },
      onCardFormApiInit: this.props.onCardFormApiInit,
      onChange: this.props.onCardFieldChange,
      revalidateAppInstance: this.props.revalidateAppInstance,
    };

    if (connectedProviderId === PaymentProvider.Square) {
      props.squareAppId = additionalInfo.squareAppId;
    } else if (connectedProviderId === PaymentProvider.TwoCheckout) {
      props.sellerId = additionalInfo.sellerId;
      props.publishableKey = additionalInfo.publishableKey;
    }

    return <CreditCard {...props} />;
  }

  renderPaymentMethodComponentByType = () => {
    const { theme, isSinglePaymentOption } = this.props;
    const { paymentMethod, name, additionalInfo, iconUrl, redirectable } =
      this.props.paymentMethod;

    const commonProps: CommonMethodProps = {
      theme,
      isSinglePaymentOption,
    };

    switch (paymentMethod) {
      case PaymentMethod.CreditCard:
        return this.renderCreditCard(commonProps);
      case PaymentMethod.Offline:
        return <Offline {...commonProps} text={additionalInfo?.text} />;
      case PaymentMethod.InPerson:
        return <InPerson {...commonProps} />;
      default:
        return (
          <Wallet
            {...commonProps}
            paymentMethodName={paymentMethod}
            paymentMethodLabel={name}
            paymentMethodIconUrl={iconUrl}
            amount={this.props.amount}
            currency={this.props.currency}
            country={this.props.countryCode}
            withTitle={this.props.isWalletWithTitle}
            withRedirect={redirectable}
            deviceType={this.props.deviceType}
          />
        );
    }
  };

  render() {
    let className: string;

    // TODO: temporary solution for old custom view.
    //       Delete after AdaptedComponents spec will be merged
    if (
      !isAdaptedComponentsEnabled(
        this.props.isMerchantContext,
        this.props.appId
      )
    ) {
      className = cn({
        [s.root]: true,
        [s.mobile]: this.props.deviceType === 'mobile',
        [s.modalTheme]: this.props.theme === Theme.modal,
        [s.creditCard]:
          this.props.paymentMethod.paymentMethod === PaymentMethod.CreditCard,
      });
    }
    return (
      <div data-hook={DH.PaymentMethod.Container} className={className}>
        {this.renderPaymentMethodComponentByType()}
      </div>
    );
  }
}
